import React from "react";
import { FieldGroupRow, FieldGroupRowStatus } from "../../../models/intake";
import FormConstants from "./DataEntryFieldsConstants";
import { writeFieldToForm } from "./formFieldHelpers";
import DateField from "../../../common/formFields/DateField";
import { DEFAULT_MIN_DATE } from "../../../common/constants/ApiConstants";
import { isAuthDateRequired } from "./validations/localValidations";

export interface AuthorizationDateFieldProps {
    /** Currently selected Authorization date (read from DATA_ENTRY_FIELDS_FORM). */
    readonly value?: Date | string;
    /** transactionDetails.requestPurpose?.id - drives isAuthDateRequired, same source the classic pane's validator already reads (not the live form purpose value). */
    readonly requestPurposeId?: number;
    /** Hides the field entirely - classic pane's requestType != DISCLOSURE_ACCOUNTING gate. */
    readonly visible: boolean;
    readonly dispatch: Function;
    readonly formName?: string;
    /**
     * Extra, non-form side effect the caller wants to run on change (the
     * classic pane's autosave PATCH). The base DATA_ENTRY_FIELDS_FORM write
     * is always handled by this component itself via writeFieldToForm -
     * callers should NOT also dispatch a redundant form write here (same
     * rule RequestTypePurposeField's onRequestTypeChange established).
     */
    readonly onAuthorizationDateChange?: (newValue?: Date | string) => void;
}

/**
 * Shared Authorization Date field (Spec #34098 / F1a). Rendered by both the
 * classic RightPane Details tab and the new Quick Entry row so both views
 * write into - and read from - the same DATA_ENTRY_FIELDS_FORM redux-form
 * instance via the shared writeFieldToForm helper.
 */
const AuthorizationDateField: React.FC<AuthorizationDateFieldProps> = (props) => {
    if (!props.visible) {
        return null;
    }

    const formName = props.formName ?? FormConstants.DATA_ENTRY_FIELDS_FORM;
    const required = isAuthDateRequired(props.requestPurposeId);

    const onChange = (value?: Date | string) => {
        writeFieldToForm(props.dispatch, FormConstants.AUTHORIZATION_DATE, value, formName);
        props.onAuthorizationDateChange?.(value);
    };

    return (
        <DateField
            label="Authorization date"
            placeholder="mm/dd/yyyy"
            minDate={DEFAULT_MIN_DATE}
            maxDate={new Date()}
            required={required}
            errMsg=""
            onChange={onChange}
            value={props.value}
        />
    );
};

export default AuthorizationDateField;

// Exported so DataEntryDetails.tsx can key its Quick Entry renderRowContent
// special-case off the same constant used to build the row.
export const AUTHORIZATION_DATE_ROW_ID = "authorizationDate";

/**
 * Builds the FieldGroupRow for the Authorization Date row. Required-ness
 * mirrors the classic pane's own isAuthDateRequired(requestPurposeId) rule;
 * NeedsEntry when required-and-empty, EmptyNotRequired when optional-and-
 * empty, Confirmed once a value exists.
 */
export function mapAuthorizationDateToFieldGroupRow(
    badgeNumber: number,
    requestPurposeId: number | undefined,
    formValue: Date | string | undefined,
): FieldGroupRow {
    const hasValue = formValue !== undefined && formValue !== null && formValue !== "";
    const required = isAuthDateRequired(requestPurposeId);

    return {
        id: AUTHORIZATION_DATE_ROW_ID,
        badgeNumber,
        status: hasValue
            ? FieldGroupRowStatus.Confirmed
            : (required ? FieldGroupRowStatus.NeedsEntry : FieldGroupRowStatus.EmptyNotRequired),
        label: "Authorization Date",
        value: hasValue ? String(formValue) : null,
        isAiPreSelected: false,
    };
}
